<template>
  <el-dialog
    :model-value="modelValue"
    :title="isCreating ? '新增小程序卡片' : '选择小程序'"
    :width="isCreating ? '500px' : '860px'"
    append-to-body
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!-- 新增表单（仅列表无数据时展示） -->
    <template v-if="isCreating">
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="100px">
        <el-form-item label="appid" prop="app_id">
          <el-input v-model="formData.app_id" placeholder="小程序appid" maxlength="128" />
        </el-form-item>
        <el-form-item label="小程序路径" prop="page_path">
          <el-input v-model="formData.page_path" placeholder="小程序路径" />
        </el-form-item>
        <el-form-item label="卡片名称" prop="title">
          <el-input v-model="formData.title" placeholder="卡片名称" maxlength="255" />
        </el-form-item>
        <el-form-item label="卡片描述" prop="description">
          <el-input v-model="formData.description" placeholder="卡片描述" maxlength="500" />
        </el-form-item>
        <el-form-item label="上传图片" prop="image_url">
          <div class="cover-wrapper">
            <el-upload
              :show-file-list="false"
              :auto-upload="true"
              :http-request="(opt: { file: File }) => handleFormImageUpload(opt)"
              accept="image/jpeg,image/png,image/jpg"
              drag
              class="cover-upload"
            >
              <img v-if="formData.image_url" :src="formData.image_url" class="cover-img" />
              <div v-else class="cover-placeholder">
                <el-icon><Camera /></el-icon>
              </div>
            </el-upload>
            <div
              v-if="formData.image_url"
              class="cover-remove"
              @click.stop="formData.image_url = ''"
            >
              <el-icon><Close /></el-icon>
            </div>
          </div>
        </el-form-item>
      </el-form>
    </template>

    <!-- 列表 -->
    <template v-else>
      <div class="font-medium text-slate-700 mb-10">物料配置</div>

      <!-- 按钮区：查看模式 / 编辑模式 -->
      <div class="flex mb-10">
        <template v-if="!isEditing">
          <el-button type="primary" @click="onAdd">
            {{ hasNewRows ? '增加多条' : '添加小程序' }}
          </el-button>
          <el-button v-if="!hasNewRows" type="primary" plain @click="onBatchEdit">
            批量编辑
          </el-button>
          <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDelete">
            删除
          </el-button>
          <el-button v-if="hasNewRows" type="primary" :loading="saveLoading" @click="onSave">
            保存
          </el-button>
          <el-button v-if="hasNewRows" @click="onCancelAdd">取消添加</el-button>
        </template>
        <template v-else>
          <el-button type="primary" plain @click="addEditRow">增加多条</el-button>
          <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteEditRow">
            删除
          </el-button>
          <el-button type="primary" :loading="saveLoading" @click="onSave">保存</el-button>
          <el-button @click="onCancelEdit">取消编辑</el-button>
        </template>
      </div>

      <DataTableWithPagination
        v-model:page="page"
        v-model:page-size="pageSize"
        :data="tableRows"
        :loading="loading"
        :total="total"
        :show-column-settings="false"
        size="small"
        padding="0"
        @page-change="loadList"
        @size-change="loadList"
        @selection-change="(rows: MiniProgramRow[]) => (selectedRows = rows)"
      >
        <el-table-column type="selection" width="44" />
        <el-table-column label="appid">
          <template #default="{ row }">
            <el-input v-if="isEditing || row.isNew" v-model="row.app_id" placeholder="appid" />
            <span v-else>{{ row.app_id }}</span>
          </template>
        </el-table-column>
        <el-table-column label="小程序路径">
          <template #default="{ row }">
            <el-input v-if="isEditing || row.isNew" v-model="row.page_path" placeholder="路径" />
            <span v-else>{{ row.page_path }}</span>
          </template>
        </el-table-column>
        <el-table-column label="卡片名称" width="120">
          <template #default="{ row }">
            <el-input v-if="isEditing || row.isNew" v-model="row.title" placeholder="名称" />
            <span v-else>{{ row.title }}</span>
          </template>
        </el-table-column>
        <el-table-column label="卡片描述" width="120">
          <template #default="{ row }">
            <el-input v-if="isEditing || row.isNew" v-model="row.description" placeholder="描述" />
            <span v-else>{{ row.description }}</span>
          </template>
        </el-table-column>
        <el-table-column label="LOGO" width="72">
          <template #default="{ row }">
            <el-upload
              v-if="isEditing || row.isNew"
              :show-file-list="false"
              :auto-upload="false"
              accept="image/jpeg,image/png,image/jpg"
              @change="(f: { raw?: File }) => f.raw && onRowImageChange(row, f.raw)"
            >
              <el-avatar shape="square" :size="32" :src="row.image_url || undefined">
                <el-icon><Plus /></el-icon>
              </el-avatar>
            </el-upload>
            <el-avatar v-else-if="row.image_url" shape="square" :size="32" :src="row.image_url" />
            <el-avatar v-else shape="square" :size="32">
              <el-icon><Picture /></el-icon>
            </el-avatar>
          </template>
        </el-table-column>
      </DataTableWithPagination>
    </template>

    <template #footer>
      <el-button @click="onCancel">取消</el-button>
      <el-button v-if="isCreating" type="primary" :loading="submitLoading" @click="submitForm">
        确认
      </el-button>
      <el-button
        v-else-if="!isEditing"
        type="primary"
        :disabled="selectedRows.filter((r) => !r.isNew).length === 0"
        @click="onConfirm"
      >
        确认
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { Camera, Close, Picture, Plus } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import { channelMaterialsApi, parseMaterialContent } from '@/api/smartService/channelMaterials';
import { getUploadUrl } from '@/api/common';
import { toBearerHeader } from '@/utils/token';
import type { MiniProgramRow } from '@/types/chatBox';

const props = defineProps<{
  modelValue: boolean;
  accountId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'confirm', item: Record<string, unknown>): void;
}>();

const MATERIAL_TYPE = 9;

// ── 列表状态 ──
const loading = ref(false);
const tableRows = ref<MiniProgramRow[]>([]);
const selectedRows = ref<MiniProgramRow[]>([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(20);

// ── 编辑状态 ──
const isEditing = ref(false);
const hasNewRows = computed(() => tableRows.value.some((r) => r.isNew));
const saveLoading = ref(false);

// ── 创建表单状态（仅列表为空时使用） ──
const isCreating = ref(false);
const submitLoading = ref(false);
const formRef = ref<InstanceType<(typeof import('element-plus'))['ElForm']>>();
const formData = ref({ app_id: '', page_path: '', title: '', description: '', image_url: '' });
const formRules = {
  app_id: [{ required: true, message: '请填写小程序appid', trigger: 'blur' }],
  page_path: [{ required: true, message: '请填写小程序路径', trigger: 'blur' }],
  title: [{ required: true, message: '请填写卡片名称', trigger: 'blur' }],
  description: [{ required: true, message: '请填写卡片描述', trigger: 'blur' }],
  image_url: [{ required: true, message: '请上传封面', trigger: 'change' }],
};

const uploadUrl = getUploadUrl();
const authToken = toBearerHeader(localStorage.getItem('token'));

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      isCreating.value = false;
      isEditing.value = false;
      selectedRows.value = [];
      page.value = 1;
      loadList();
      nextTick(() => formRef.value?.clearValidate());
    } else {
      formRef.value?.clearValidate();
    }
  },
);

function rowFromMaterial(r: Record<string, unknown>): MiniProgramRow {
  const c = parseMaterialContent(r.content);
  return {
    id: r.id as number | undefined,
    app_id: (c.app_id as string) || '',
    page_path: (c.page_path as string) || '',
    title: (c.title as string) || (r.name as string) || '',
    description: (c.description as string) || '',
    image_url: (c.image_url as string) || (c.thumb_url as string) || '',
  };
}

async function loadList() {
  loading.value = true;
  try {
    const { list, total: t } = await channelMaterialsApi
      .list({
        account_id: props.accountId || '',
        type: MATERIAL_TYPE,
        page: page.value,
        per_page: pageSize.value,
      })
      .catch(() => ({ list: [], total: 0 }));
    tableRows.value = (list as Record<string, unknown>[]).map(rowFromMaterial);
    total.value = t;
    if (tableRows.value.length === 0 && page.value === 1 && !isCreating.value) {
      isCreating.value = true;
      formData.value = { app_id: '', page_path: '', title: '', description: '', image_url: '' };
    }
  } finally {
    loading.value = false;
  }
}

// ── 查看模式操作 ──

function emptyRow(): MiniProgramRow {
  return {
    app_id: '',
    page_path: '',
    title: '',
    description: '',
    image_url: '',
    isNew: true,
  };
}

function onAdd() {
  tableRows.value.unshift(emptyRow());
}

function onBatchEdit() {
  isEditing.value = true;
  selectedRows.value = [];
}

async function onDelete() {
  if (selectedRows.value.length === 0) return;
  const ids = selectedRows.value.map((r) => r.id).filter((v): v is number => typeof v === 'number');
  if (ids.length === 0) {
    ElMessage.warning('请选择已保存的数据');
    return;
  }
  await ElMessageBox.confirm('确定删除选中的小程序卡片吗？', '提示', { type: 'warning' });
  await Promise.all(ids.map((id) => channelMaterialsApi.delete(id)));
  ElMessage.success('删除成功');
  selectedRows.value = [];
  loadList();
}

// ── 编辑模式操作 ──

function addEditRow() {
  tableRows.value.push(emptyRow());
}

function onDeleteEditRow() {
  tableRows.value = tableRows.value.filter((r) => !selectedRows.value.includes(r));
  selectedRows.value = [];
}

async function onRowImageChange(row: MiniProgramRow, file: File) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: { Authorization: authToken || '' },
    body: form,
  });
  const data = (await res.json()) as { code?: number; data?: { url?: string } };
  if (data?.code === 0 && data?.data?.url) {
    row.image_url = data.data.url;
  }
}

function buildContent(row: MiniProgramRow) {
  return {
    app_id: row.app_id,
    page_path: row.page_path,
    title: row.title,
    description: row.description,
    image_url: row.image_url,
  };
}

async function onSave() {
  const rows = tableRows.value.filter(
    (r) => r.app_id.trim() || r.title.trim() || r.description.trim(),
  );
  if (rows.length === 0) {
    ElMessage.warning('请至少填写一条数据');
    return;
  }
  const newRows = rows.filter((r) => r.isNew);
  // 仅在批量编辑模式下才更新已存在记录；新增模式下不动已有数据
  const editedRows = isEditing.value ? rows.filter((r) => !r.isNew && r.id) : [];
  if (newRows.length === 0 && editedRows.length === 0) {
    isEditing.value = false;
    loadList();
    return;
  }
  // 校验必填
  const requiredFields = ['app_id', 'page_path', 'title', 'description', 'image_url'] as const;
  const fieldLabels: Record<string, string> = {
    app_id: 'appid',
    page_path: '小程序路径',
    title: '卡片名称',
    description: '卡片描述',
    image_url: 'LOGO',
  };
  for (let i = 0; i < newRows.length; i++) {
    const r = newRows[i]!;
    for (const field of requiredFields) {
      if (!String(r[field] ?? '').trim()) {
        ElMessage.warning(`第${i + 1}条新增数据：${fieldLabels[field]}不能为空`);
        return;
      }
    }
  }
  saveLoading.value = true;
  try {
    await Promise.all([
      ...newRows.map((row) =>
        channelMaterialsApi.create({
          account_id: props.accountId || '',
          name: row.title || '小程序卡片',
          type: MATERIAL_TYPE,
          content: buildContent(row),
        }),
      ),
      ...editedRows.map((row) =>
        channelMaterialsApi.update(row.id!, {
          name: row.title || '小程序卡片',
          content: buildContent(row),
        }),
      ),
    ]);
    ElMessage.success('保存成功');
    isEditing.value = false;
    selectedRows.value = [];
    loadList();
  } finally {
    saveLoading.value = false;
  }
}

function onCancelEdit() {
  isEditing.value = false;
  loadList();
}

function onCancelAdd() {
  tableRows.value = tableRows.value.filter((r) => !r.isNew);
}

// ── 选择确认 ──

function onConfirm() {
  const item = selectedRows.value.find((r) => !r.isNew);
  if (!item) {
    ElMessage.warning('请勾选已保存的小程序卡片');
    return;
  }
  emit('confirm', { ...buildContent(item), id: item.id } as Record<string, unknown>);
  emit('update:modelValue', false);
}

function onCancel() {
  if (isEditing.value) {
    isEditing.value = false;
    loadList();
  } else {
    emit('update:modelValue', false);
  }
}

// ── 创建表单（列表为空时） ──

async function handleFormImageUpload(opt: { file: File }): Promise<void> {
  const form = new FormData();
  form.append('file', opt.file);
  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: { Authorization: authToken || '' },
    body: form,
  });
  const data = (await res.json()) as { code?: number; data?: { url?: string } };
  if (data?.code === 0 && data?.data?.url) {
    formData.value.image_url = data.data.url;
  }
}

function submitForm() {
  const form = formRef.value as { validate: (cb: (ok: boolean) => void) => void } | undefined;
  if (!form) return;
  form.validate(async (ok: boolean) => {
    if (!ok) return;
    submitLoading.value = true;
    try {
      await channelMaterialsApi.create({
        account_id: props.accountId || '',
        name: formData.value.title || '小程序卡片',
        type: MATERIAL_TYPE,
        content: { ...formData.value },
      });
      ElMessage.success('创建成功');
      isCreating.value = false;
      loadList();
    } finally {
      submitLoading.value = false;
    }
  });
}
</script>

<style lang="scss" scoped>
.cover-wrapper {
  position: relative;
  display: inline-block;
}
.cover-upload {
  :deep(.el-upload-dragger) {
    width: 160px;
    height: 90px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.cover-img {
  width: 160px;
  height: 90px;
  object-fit: cover;
}
.cover-placeholder {
  font-size: 24px;
  color: #c0c4cc;
}
.cover-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f56c6c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
}
</style>
